import { ref } from 'vue';
import _ from 'lodash';
import { useFolderTree } from './useFolderTree';

export const sortFoldersByHierarchy = (list) => {
  if (!Array.isArray(list)) {
    return [];
  }
  const childrenByParent = {};
  list.forEach((f) => {
    const pid = f.parent_id === undefined || f.parent_id === null
      ? '0'
      : String(f.parent_id);
    if (!childrenByParent[pid]) {
      childrenByParent[pid] = [];
    }
    childrenByParent[pid].push(f);
  });
  Object.keys(childrenByParent).forEach((pid) => {
    childrenByParent[pid].sort((a, b) => {
      const so = Number(a.sort_order || 0) - Number(b.sort_order || 0);
      if (so !== 0) return so;
      return Number(a.id) - Number(b.id);
    });
  });

  const sorted = [];
  const visited = new Set();
  const dfs = (parentKey) => {
    const children = childrenByParent[parentKey] || [];
    children.forEach((child) => {
      sorted.push(child);
      visited.add(child);
      dfs(String(child.id));
    });
  };
  dfs('0');

  list.forEach((f) => {
    if (!visited.has(f)) {
      sorted.push(f);
    }
  });

  return sorted;
};

export const useFolderCreate = ({ folders, selectedFolderId, allowedFolderIds, onCreated }) => {
  const parentId = ref(null);
  const folderName = ref('');
  const submitting = ref(false);
  const errors = ref({});

  const translate = (key, fallback = '') =>
    _.get(window.i18n, key) || fallback || key;

  // モーダル専用の折りたたみツリー状態。サイドバー(useFolderTree(folders, selectedFolderId))とは独立し、localStorageも汚さない
  const {
    topLevelFolders: treeTopLevelFolders,
    getChildren: treeGetChildren,
    isCollapsed: treeIsCollapsed,
    toggleFolder: treeToggleFolder,
    expandAncestorsOf: treeExpandAncestorsOf,
    scrollSelectedFolderIntoView: treeScrollSelectedFolderIntoView,
  } = useFolderTree(folders, parentId, {
    persist: false,
    initialCollapse: 'collapseAll',
    scrollContainerSelector: '.folder-picker-tree',
  });

  const open = () => {
    const currentId = selectedFolderId.value;
    const parsed = Number(currentId);
    parentId.value = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    folderName.value = '';
    errors.value = {};
    treeExpandAncestorsOf(parentId.value);
  };

  const scrollToSelectedParent = () => {
    treeScrollSelectedFolderIntoView(parentId.value);
  };

  const selectParent = (folderId) => {
    parentId.value = folderId;
  };

  const resolveDepth = () => {
    if (parentId.value === null || parentId.value === 0) {
      return 1;
    }
    const list = Array.isArray(folders.value) ? folders.value : [];
    const parent = list.find((f) => String(f.id) === String(parentId.value));
    return parent ? Number(parent.depth) + 1 : 1;
  };

  const resolveSortOrder = (depth, pid) => {
    const list = Array.isArray(folders.value) ? folders.value : [];
    const effectiveParentId = depth === 1 ? null : pid;
    const siblings = list.filter((f) => {
      const fp = f.parent_id === undefined || f.parent_id === null ? 0 : Number(f.parent_id);
      const ep = effectiveParentId === null ? 0 : Number(effectiveParentId);
      return fp === ep;
    });
    if (siblings.length === 0) {
      return 1;
    }
    const maxSortOrder = siblings.reduce((max, f) => {
      const v = Number(f.sort_order);
      return Number.isFinite(v) && v > max ? v : max;
    }, 0);
    return maxSortOrder + 1;
  };

  const submit = async () => {
    if (!folderName.value.trim()) {
      errors.value = {
        name: [translate('folder.name_required', 'フォルダ名を入力してください。')],
      };
      return;
    }

    const depth = resolveDepth();
    const pid = depth === 1 ? null : parentId.value;

    // 非admin(allowedFolderIdsが配列)はルート直下へのフォルダ作成不可。バックエンド(FolderController::store)と同じ判定
    if (Array.isArray(allowedFolderIds?.value) && pid === null) {
      errors.value = {
        parent_id: [translate('folder.create_root_not_allowed', 'ルート直下にはフォルダを作成できません。')],
      };
      return;
    }

    const sortOrder = resolveSortOrder(depth, pid);

    submitting.value = true;
    errors.value = {};

    try {
      const response = await window.axios.post(
        '/dashboard/folders',
        {
          name: folderName.value.trim(),
          depth,
          parent_id: pid,
          sort_order: sortOrder,
        },
        { headers: { Accept: 'application/json' } }
      );

      const newFolder = response.data.folder;
      submitting.value = false;
      if (typeof onCreated === 'function') {
        onCreated(newFolder, response.data.message || '');
      }
    } catch (error) {
      submitting.value = false;
      if (error.response && error.response.status === 422) {
        errors.value = error.response.data.errors || {};
      } else if (error.response && error.response.data && error.response.data.message) {
        errors.value = {
          parent_id: [error.response.data.message],
        };
      } else {
        errors.value = {
          name: [translate('folder.create_failed', 'フォルダの作成に失敗しました。')],
        };
      }
    }
  };

  return {
    parentId,
    folderName,
    submitting,
    errors,
    treeTopLevelFolders,
    treeGetChildren,
    treeIsCollapsed,
    treeToggleFolder,
    selectParent,
    open,
    scrollToSelectedParent,
    submit,
  };
};
